import type { LucideIcon } from 'lucide-react';
import { LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { formatMoney, formatSignedMoney } from '@domain/money';

/* ---------------------------------------------------------------- money --- */

/**
 * Renders minor units as currency. With `signed`, positive reads as "is owed"
 * (success) and negative as "owes" (danger) — the colour convention used
 * everywhere balances appear.
 */
export function Money({
  minor,
  currency,
  signed = false,
  className,
  muted = false,
}: {
  minor: number;
  currency: string;
  signed?: boolean;
  className?: string;
  muted?: boolean;
}) {
  const tone = !signed
    ? muted
      ? 'text-muted'
      : ''
    : minor > 0
      ? 'text-success'
      : minor < 0
        ? 'text-danger'
        : 'text-muted';

  return (
    <span className={cn('tnum tabular-nums', tone, className)}>
      {signed ? formatSignedMoney(minor, currency) : formatMoney(minor, currency)}
    </span>
  );
}

export function balancePhrase(minor: number, currency: string): string {
  if (minor === 0) return 'settled up';
  return minor > 0
    ? `gets back ${formatMoney(minor, currency)}`
    : `owes ${formatMoney(-minor, currency)}`;
}

/* --------------------------------------------------------------- avatar --- */

const SWATCHES = [
  '#6366F1', '#8B5CF6', '#EC4899', '#F97316',
  '#10B981', '#14B8A6', '#0EA5E9', '#F43F5E',
];

export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return SWATCHES[hash % SWATCHES.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_SIZES = {
  xs: 'size-6 text-[9px]',
  sm: 'size-8 text-[11px]',
  md: 'size-10 text-[12px]',
  lg: 'size-16 text-lg',
} as const;

export function Avatar({
  name,
  id,
  size = 'md',
  dimmed = false,
  className,
}: {
  name: string;
  /** Colour seed — pass the member id so renames don't change the colour. */
  id?: string;
  size?: keyof typeof AVATAR_SIZES;
  dimmed?: boolean;
  className?: string;
}) {
  return (
    <span
      title={name}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold text-white',
        AVATAR_SIZES[size],
        dimmed && 'opacity-45',
        className
      )}
      style={{
        backgroundImage: `linear-gradient(135deg, ${avatarColor(id ?? name)}, color-mix(in oklab, ${avatarColor(id ?? name)} 70%, #000))`,
      }}
    >
      {initials(name)}
    </span>
  );
}

export function AvatarStack({
  people,
  max = 5,
}: {
  people: { id: string; name: string }[];
  max?: number;
}) {
  const shown = people.slice(0, max);
  const overflow = people.length - shown.length;

  return (
    <span className="inline-flex items-center">
      {shown.map((person, index) => (
        <Avatar
          key={person.id}
          name={person.name}
          id={person.id}
          size="xs"
          className={cn('ring-2 ring-surface', index > 0 && '-ml-1.5')}
        />
      ))}
      {overflow > 0 && (
        <span className="-ml-1.5 inline-flex size-6 items-center justify-center rounded-full bg-elevated text-[9px] font-bold text-muted ring-2 ring-surface">
          +{overflow}
        </span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- badge --- */

const BADGE_TONES = {
  neutral: 'bg-elevated text-muted ring-1 ring-inset ring-line',
  primary: 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20',
  success: 'bg-success/10 text-success ring-1 ring-inset ring-success/20',
  danger: 'bg-danger/10 text-danger ring-1 ring-inset ring-danger/20',
  warning: 'bg-warning/10 text-warning ring-1 ring-inset ring-warning/20',
} as const;

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONES;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        BADGE_TONES[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------ empty/load --- */

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-16 text-center', className)}>
      {Icon && (
        <span className="relative mb-1 inline-flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-8 ring-primary/5">
          <Icon size={24} strokeWidth={2} />
        </span>
      )}
      <h3 className="text-base font-semibold tracking-tight">{title}</h3>
      {description && <p className="max-w-md text-sm leading-relaxed text-muted">{description}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 py-16 text-muted">
      <LoaderCircle size={22} className="animate-spin text-primary" />
      <p className="text-[13px]">{label}</p>
    </div>
  );
}
